import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { AppState } from '../../../store';
import { onGetAllProducts } from '../../../store/actions/productActions';
import LinearProgressBar from '../../../common/LinearProgress';
import { dateFormatToLocalString } from '../../../utils';
import { showErrorMessage, showSuccessMessage } from '../../../common/Toaster';
import jwtAxios from '../../../services/axiosInstance/ApiConfig';
import { getHeaders } from '../../../store/actions/Auth';
import { SET_ALL_PRODUCTS } from '../../../store/types/actions/ProductAction';
import { onGetAllCustomers } from '../../../store/actions/TopSeller';

interface Column {
	id: 'id' | 'name' | 'email'| 'created' | 'updated' | 'actions';
	label: string;
	minWidth?: number;
	align?: 'left' | 'right' | 'center';
	format?: (value: number) => string;
}

const columns: readonly Column[] = [
	{ id: 'id', label: 'Seller Id', minWidth: 50 },
	{ id: 'name', label: 'Name', minWidth: 170 },
    { id: 'email', label: 'Email', minWidth: 170 },
    { id: 'created', label: 'Created date', minWidth: 170 },
    { id: 'updated', label: 'Updated date', minWidth: 170 },
	{ id: 'actions', label: 'Actions', minWidth: 170, align: 'center' },
];

interface RowType {
	id: string;
	name: string;
    email: string;
    created: string;
    updated: string;
	actions: string;
}

function createRows(
	id: string,
	name: string,
    email: string,
    created: string,
    updated: string,
	actions: string,
): RowType {
	return { id, name, email, created, updated, actions };
}

interface CustomersListProps {
	handleSelectedCustomer: (userId: string) => void
}

export default function CustomersList(props: CustomersListProps) {
	const dispatch = useDispatch()
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);

	const {allCustomers} = useSelector<AppState, AppState['products']>(
        ({products}) => products,
    );

	const {loading} = useSelector<AppState, AppState['common']>(
        ({common}) => common,
    );

	useEffect(() => {
		if(allCustomers && allCustomers.length > 0) {
			return 
		} else {
			dispatch({ type: SET_ALL_PRODUCTS, payload: null });
			dispatch(onGetAllCustomers())
		}
	}, [dispatch, allCustomers])

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	const handleDeleteCustomer = (rowId: string) => {
		jwtAxios.delete('/users/deleteCustomer/' + rowId, { headers: getHeaders()})
		.then(response => {
			showSuccessMessage(response.data.message)
			dispatch(onGetAllProducts({}))
		}).catch((err: any) => {
			showErrorMessage(err)
		})
	}

	const rows: any =
		allCustomers &&
		allCustomers.length > 0 
		? allCustomers.map((user: any) => createRows(
			user._id,
			user.name,
			user.email,
			user.createdAt,
			user.updatedAt,
			user.actions
		)) 
		: [];

	return (
		<Paper sx={{ width: '100%', overflow: 'hidden', padding: 0 }}>
			<TableContainer>
				{loading && <LinearProgressBar />}
				<Table stickyHeader aria-label="sticky table">
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell
									key={column.id}
									align={column.align}
									style={{ minWidth: column.minWidth, fontWeight: 600 }}
								>
									{column.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows
							.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
							.map((row: RowType) => {
								return (
									<TableRow hover role="checkbox" tabIndex={-1} key={row.name}>
										{columns.map((column) => {
											const col_id = column.id
											let value = row[col_id];
											if(col_id === 'created' || col_id === 'updated') {
												value = dateFormatToLocalString(row[col_id])
											}
											return (
												<TableCell key={column.id} align={column.align}>
													{col_id === 'actions' &&
														<Box display="flex" flexDirection="row">
															<Button
																color="info"
																variant="outlined"
																size="small"
																style={{marginRight: '8px'}}
																onClick={()=> props.handleSelectedCustomer(row.id)}
															>
																Edit
															</Button>
															<Button
																color="error"
																variant="outlined"
																size="small"
																onClick={()=> handleDeleteCustomer(row.id)}
															>
																Delete
															</Button>
														</Box>
													}
													{col_id !== 'actions' && column.format && typeof value === 'number'
														? column.format(value)
														: value}
												</TableCell>
											);
										})}
									</TableRow>
								);
							})}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[7, 21, 100]}
				component="div"
				count={rows.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</Paper>
	);
}
